import { FC, useState } from 'react';
import { inputFieldStyles, textareaStyles } from '../../../global-styles';
import { Pencil, Check } from 'lucide-react';
import Button from '../../common/Button';
import { ButtonSize, ButtonVariant } from '../../../types/button.enum';
import QuizService from '../../../services/quiz.service';
import { useAppDispatch } from '../../../hooks/redux-hooks';
import { setToastInfo } from '../../../store/common.slice';
import { ToastType } from '../../../types/common.types';

interface QuizQuestion {
  questionNumber: number;
  question?: string;
  statement?: string;
  options?: Record<string, string>;
  correctAnswer?: string;
  expectedAnswer?: string;
  modelAnswer?: string;
  keyPoints?: string[];
  gradingCriteria?: string[];
  answers?: string[];
  alternativeAnswers?: string[][];
  explanation?: string;
  difficulty?: string;
  marks?: number;
  type?: string;
}

interface QuizContentProps {
  testContent: string[];
  questionType: string;
  quizId: string;
  onUpdate?: (updatedContent: string[]) => void;
  readOnly?: boolean;
}

const parseQuestion = (item: string): QuizQuestion | null => {
  try {
    return JSON.parse(item);
  } catch {
    return null;
  }
};

const getQuestionText = (q: QuizQuestion): string => {
  return q.question || q.statement || '';
};

const getDisplayType = (q: QuizQuestion, fallbackType: string): string => {
  if (q.type) return q.type;
  if (q.options) return q.correctAnswer?.includes(',') ? 'Multi Select' : 'Single Choice';
  if (q.statement !== undefined) return 'True / False';
  if (q.expectedAnswer !== undefined) return 'Short Answer';
  if (q.modelAnswer !== undefined || q.gradingCriteria) return 'Subjective';
  if (q.answers) return 'Fill in the Blank';

  const typeMap: Record<string, string> = {
    'multiple choice': 'Multiple Choice',
    'true false': 'True / False',
    'short answer': 'Short Answer',
    essay: 'Essay',
    subjective: 'Subjective',
    objective: 'Objective',
    'fill in the blank': 'Fill in the Blank',
  };
  return typeMap[fallbackType.toLowerCase()] || fallbackType;
};

const getMarks = (q: QuizQuestion): number => {
  if (q.marks) return q.marks;
  if (q.modelAnswer || q.gradingCriteria) return 10;
  return 5;
};

// Editable question card
const QuestionCard: FC<{
  question: QuizQuestion;
  index: number;
  questionType: string;
  onSave: (updated: QuizQuestion) => Promise<void>;
  onDelete: () => void;
  readOnly?: boolean;
}> = ({ question, index, questionType, onSave, onDelete, readOnly }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState<QuizQuestion>({ ...question });

  const displayType = getDisplayType(question, questionType);
  const marks = getMarks(question);
  const isDirty = JSON.stringify(editData) !== JSON.stringify(question);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editData);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({ ...question });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="border border-[#E2E8F0] rounded-lg p-5 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#333] text-white flex items-center justify-center text-[13px] font-medium">
              {index + 1}
            </span>
            <span className="text-[13px] font-medium text-[#666]">
              Edit Question
            </span>
          </div>
          <div className="flex items-center">
            <Button
              type="button"
              onClick={handleSave}
              size={ButtonSize.SMALL}
              disabled={saving || !isDirty}
            >
              {saving ? 'Saving...' : 'Update'}
            </Button>
            <Button
              type="button"
              onClick={handleCancel}
              size={ButtonSize.SMALL}
              variant={ButtonVariant.TERTIARY}
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Question text */}
        <div className="mb-3">
          <label className="text-[12px] font-medium text-[#666] mb-1 block">
            Question
          </label>
          <textarea
            className={`${textareaStyles} min-h-[60px]`}
            value={editData.question || editData.statement || ''}
            onChange={(e) => {
              if (editData.statement !== undefined) {
                setEditData({ ...editData, statement: e.target.value });
              } else {
                setEditData({ ...editData, question: e.target.value });
              }
            }}
          />
        </div>

        {/* Options for multiple choice */}
        {editData.options && (
          <div className="mb-3">
            <label className="text-[12px] font-medium text-[#666] mb-1 block">
              Options
            </label>
            {Object.entries(editData.options).map(([key, val]) => (
              <div key={key} className="flex items-center gap-2 mb-2">
                <span className="text-[13px] font-medium text-[#333] w-5">
                  {key}.
                </span>
                <input
                  type="text"
                  className={inputFieldStyles}
                  value={val}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      options: { ...editData.options, [key]: e.target.value },
                    })
                  }
                />
                <label className="flex items-center gap-1 text-[12px] text-[#666] whitespace-nowrap cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editData.correctAnswer?.split(',').map(s => s.trim()).includes(key) ?? false}
                    onChange={() => {
                      const current = editData.correctAnswer?.split(',').map(s => s.trim()).filter(Boolean) ?? [];
                      const updated = current.includes(key)
                        ? current.filter(k => k !== key)
                        : [...current, key].sort();
                      setEditData({ ...editData, correctAnswer: updated.join(',') });
                    }}
                    className="accent-[#22c55e]"
                  />
                  Correct
                </label>
              </div>
            ))}
          </div>
        )}

        {/* True/False correct answer */}
        {editData.statement !== undefined && (
          <div className="mb-3">
            <label className="text-[12px] font-medium text-[#666] mb-1 block">
              Correct Answer
            </label>
            <div className="flex gap-4">
              {['True', 'False'].map((val) => (
                <label
                  key={val}
                  className="flex items-center gap-1.5 text-[13px] cursor-pointer"
                >
                  <input
                    type="radio"
                    name={`tf-${index}`}
                    checked={editData.correctAnswer === val}
                    onChange={() =>
                      setEditData({ ...editData, correctAnswer: val })
                    }
                    className="accent-[#22c55e]"
                  />
                  {val}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Expected answer for short answer */}
        {editData.expectedAnswer !== undefined && (
          <div className="mb-3">
            <label className="text-[12px] font-medium text-[#666] mb-1 block">
              Expected Answer
            </label>
            <textarea
              className={`${textareaStyles} min-h-[60px]`}
              value={editData.expectedAnswer}
              onChange={(e) =>
                setEditData({ ...editData, expectedAnswer: e.target.value })
              }
            />
          </div>
        )}

        {/* Model answer for essay/subjective */}
        {editData.modelAnswer !== undefined && (
          <div className="mb-3">
            <label className="text-[12px] font-medium text-[#666] mb-1 block">
              Answer
            </label>
            <textarea
              className={`${textareaStyles} min-h-[80px]`}
              value={editData.modelAnswer}
              onChange={(e) =>
                setEditData({ ...editData, modelAnswer: e.target.value })
              }
            />
          </div>
        )}

        {/* Fill in the blank answers */}
        {editData.answers && (
          <div className="mb-3">
            <label className="text-[12px] font-medium text-[#666] mb-1 block">
              Answers
            </label>
            {editData.answers.map((ans, i) => (
              <input
                key={i}
                type="text"
                className={`${inputFieldStyles} mb-2`}
                value={ans}
                onChange={(e) => {
                  const newAnswers = [...editData.answers!];
                  newAnswers[i] = e.target.value;
                  setEditData({ ...editData, answers: newAnswers });
                }}
              />
            ))}
          </div>
        )}

        {/* Explanation */}
        {editData.explanation !== undefined && (
          <div className="mb-3">
            <label className="text-[12px] font-medium text-[#666] mb-1 block">
              Explanation
            </label>
            <textarea
              className={`${textareaStyles} min-h-[50px]`}
              value={editData.explanation}
              onChange={(e) =>
                setEditData({ ...editData, explanation: e.target.value })
              }
            />
          </div>
        )}
      </div>
    );
  }

  // Read-only view
  return (
    <div className="border border-[#E2E8F0] rounded-lg px-3 py-4 bg-white hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <span className="w-8 h-8 rounded-full bg-[#22c55e] text-white flex items-center justify-center text-[13px] font-semibold shrink-0 mt-0.5">
            {index + 1}
          </span>
          <p className="text-[14px] text-[#020817] leading-relaxed flex-1">
            {getQuestionText(question)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <span className="text-[11px] font-medium text-[#22c55e] bg-[#f0fdf4] px-2.5 py-1 rounded-full">
            {displayType}
          </span>
          {/* <span className="text-[11px] font-medium text-[#666] bg-[#f5f5f5] px-2.5 py-1 rounded-full">
            {marks} marks
          </span> */}
          {!readOnly && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-1.5 rounded-md hover:bg-[#f5f5f5] text-[#999] hover:text-[#333] transition-colors"
              title="Edit"
            >
              <Pencil size={14} />
            </button>
          )}
          {/* <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded-md hover:bg-[#fef2f2] text-[#999] hover:text-[#ef4444] transition-colors"
            title="Delete"
          >
            <Trash2 size={14} />
          </button> */}
        </div>
      </div>

      {/* Multiple choice options */}
      {question.options && (
        <div className="ml-11 flex flex-col gap-2">
          {Object.entries(question.options).map(([key, val]) => {
            const isCorrect = question.correctAnswer?.split(',').map(s => s.trim()).includes(key) ?? false;
            return (
              <div
                key={key}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${
                  isCorrect
                    ? 'border-[#22c55e] bg-[#f0fdf4]'
                    : 'border-[#E2E8F0] bg-white'
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isCorrect ? 'border-[#22c55e]' : 'border-[#d1d5db]'
                  }`}
                >
                  {isCorrect && <Check size={12} className="text-[#22c55e]" />}
                </div>
                <span
                  className={`text-[13px] flex-1 ${isCorrect ? 'text-[#020817] font-medium' : 'text-[#333]'}`}
                >
                  {val}
                </span>
                {isCorrect && (
                  <span className="text-[12px] font-medium text-[#22c55e]">
                    Correct
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* True/False display */}
      {question.statement !== undefined && !question.options && (
        <div className="ml-11 flex flex-col gap-2">
          {['True', 'False'].map((val) => {
            const isCorrect = question.correctAnswer === val;
            return (
              <div
                key={val}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${
                  isCorrect
                    ? 'border-[#22c55e] bg-[#f0fdf4]'
                    : 'border-[#E2E8F0] bg-white'
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isCorrect ? 'border-[#22c55e]' : 'border-[#d1d5db]'
                  }`}
                >
                  {isCorrect && <Check size={12} className="text-[#22c55e]" />}
                </div>
                <span
                  className={`text-[13px] flex-1 ${isCorrect ? 'text-[#020817] font-medium' : 'text-[#333]'}`}
                >
                  {val}
                </span>
                {isCorrect && (
                  <span className="text-[12px] font-medium text-[#22c55e]">
                    Correct
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Short answer */}
      {question.expectedAnswer !== undefined && (
        <div className="ml-11 mt-1">
          <div className="bg-[#f0fdf4] border border-[#E2E8F0] rounded-lg px-4 py-3">
            <p className="text-[12px] font-medium text-[#666] mb-1">
              Expected Answer
            </p>
            <p className="text-[13px] text-[#333]">{question.expectedAnswer}</p>
          </div>
          {question.keyPoints && question.keyPoints.length > 0 && (
            <div className="mt-2 px-4">
              <p className="text-[12px] font-medium text-[#666] mb-1">
                Key Points
              </p>
              <ul className="list-disc list-inside">
                {question.keyPoints.map((kp, i) => (
                  <li key={i} className="text-[12px] text-[#666]">
                    {kp}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Essay / Subjective - student answer placeholder */}
      {question.modelAnswer !== undefined && (
        <div className="ml-11 mt-1">
          <div className="bg-[#f9fafb] border border-dashed border-[#d1d5db] rounded-lg px-4 py-4 text-center">
            <p className="text-[13px] text-[#999] italic">
              Students will type their answer here
            </p>
          </div>
        </div>
      )}

      {/* Fill in the blank answers */}
      {question.answers && (
        <div className="ml-11 mt-1">
          <div className="bg-[#f0fdf4] border border-[#E2E8F0] rounded-lg px-4 py-3">
            <p className="text-[12px] font-medium text-[#666] mb-1">Answers</p>
            <div className="flex flex-wrap gap-2">
              {question.answers.map((ans, i) => (
                <span
                  key={i}
                  className="text-[13px] text-[#22c55e] font-medium bg-white border border-[#22c55e] rounded-md px-3 py-1"
                >
                  {ans}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const QuizContent: FC<QuizContentProps> = ({
  testContent,
  questionType,
  quizId,
  onUpdate,
  readOnly,
}) => {
  const dispatch = useAppDispatch();
  const questions: QuizQuestion[] = testContent
    .map(parseQuestion)
    .filter((q): q is QuizQuestion => q !== null);

  if (questions.length === 0) {
    return null;
  }

  const handleQuestionSave = async (index: number, updated: QuizQuestion) => {
    try {
      const response = await QuizService.updateQuizQuestion(
        quizId,
        index,
        updated
      );
      if (response.data.data?.quiz) {
        const quiz = response.data.data.quiz;
        if (onUpdate) {
          onUpdate(quiz.testContent || []);
        }
        dispatch(
          setToastInfo({
            isOpen: true,
            message: 'Question updated successfully.',
            type: ToastType.SUCCESS,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Error updating question.',
            type: ToastType.ERROR,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          isOpen: true,
          message: err?.response?.data?.message || 'Error updating question.',
          type: ToastType.ERROR,
        })
      );
    }
  };

  const handleQuestionDelete = (index: number) => {
    if (!onUpdate) return;
    const newContent = testContent.filter((_, i) => i !== index);
    onUpdate(newContent);
  };

  // const totalMarks = questions.reduce((sum, q) => sum + getMarks(q), 0);

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-[16px] font-semibold text-[#020817]">
            Quiz Questions
          </h4>
        </div>
        {/* <span className="text-[13px] font-medium text-[#666]">
          Total: {totalMarks} marks
        </span> */}
        <span className="text-[12px] text-[#666] bg-[#f5f5f5] px-2 py-0.5 rounded-full">
          {questions.length} questions
        </span>
      </div>

      {/* Questions list */}
      <div className="flex flex-col gap-4">
        {questions.map((q, i) => (
          <QuestionCard
            key={i}
            question={q}
            index={i}
            questionType={questionType}
            onSave={(updated) => handleQuestionSave(i, updated)}
            onDelete={() => handleQuestionDelete(i)}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
};

export default QuizContent;
